import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useBulkAddressIssues } from "@/hooks/useValidation";
import type { ResolutionType, ResolutionRequest } from "@/types/validation";
import { toast } from "sonner";
import type { IssueGroup } from "./GroupedIssuesTable";

interface BulkResolveDialogProps {
  group: IssueGroup | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BulkResolveDialog({
  group,
  open,
  onOpenChange,
}: BulkResolveDialogProps) {
  const [resolutionType, setResolutionType] = useState<ResolutionType>("fixed");
  const [resolutionNotes, setResolutionNotes] = useState("");

  const bulkAddressMutation = useBulkAddressIssues();

  if (!group) return null;

  const unresolvedIssues = group.issues.filter(
    (i) => i.status === "unresolved",
  );

  const handleBulkResolve = async () => {
    const resolution: ResolutionRequest = {
      status: "resolved",
      resolution_type: resolutionType,
    };
    if (resolutionNotes) {
      resolution.resolution_notes = resolutionNotes;
    }

    try {
      await bulkAddressMutation.mutateAsync({
        issues: unresolvedIssues.map((i) => ({ id: i.id })),
        resolution,
      });
      toast.success(
        `Resolved ${unresolvedIssues.length} issue${unresolvedIssues.length === 1 ? "" : "s"}`,
      );
      setResolutionNotes("");
      onOpenChange(false);
    } catch (error) {
      toast.error(
        `Failed to resolve: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Bulk Resolve
            <Badge
              variant={group.level === "error" ? "destructive" : "secondary"}
            >
              {group.level}
            </Badge>
            <Badge variant="outline" className="uppercase text-xs">
              {group.source}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-muted-foreground">Message</Label>
            <p className="text-sm mt-1 whitespace-pre-wrap break-words bg-muted p-3 rounded">
              {group.message}
            </p>
          </div>

          <p className="text-sm text-muted-foreground">
            Resolving {unresolvedIssues.length} unresolved issue
            {unresolvedIssues.length === 1 ? "" : "s"} with the same resolution.
          </p>

          <div>
            <Label htmlFor="bulk-resolution-type">Resolution Type</Label>
            <Select
              value={resolutionType}
              onValueChange={(v) => setResolutionType(v as ResolutionType)}
            >
              <SelectTrigger id="bulk-resolution-type" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">Fixed</SelectItem>
                <SelectItem value="false_positive">False Positive</SelectItem>
                <SelectItem value="wont_fix">Won't Fix</SelectItem>
                <SelectItem value="needs_investigation">
                  Needs Investigation
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="bulk-resolution-notes">
              Resolution Notes (optional)
            </Label>
            <Textarea
              id="bulk-resolution-notes"
              placeholder="Add any notes about how these were resolved..."
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              className="mt-1"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkResolve}
            disabled={
              bulkAddressMutation.isPending || unresolvedIssues.length === 0
            }
          >
            {bulkAddressMutation.isPending && (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            )}
            Resolve All ({unresolvedIssues.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
